"use client"

import { useEffect, useState } from "react"
import { getPrivacyPolicy } from "../services/api"

export interface PrivacyPolicyData {
  id: string
  title: string
  content: string
  created_at: string
  updated_at: string
}

// Helper function to unescape HTML and add necessary classes
const enhanceHtmlContent = (html: string) => {
  if (!html) return ""

  // First unescape the JSON string if needed
  const unescapedHtml = html.replace(/\\([\s\S])|(")/g, "$1$2")

  // Add classes to elements that need styling
  const enhancedHtml = unescapedHtml
    // Style h1 elements
    .replace(/<h1>/g, '<h1 class="text-2xl font-serif text-[#b05d5d] mb-4">')

    // Style h2 elements with arrow
    .replace(
      /<h2>→/g,
      '<h2 class="text-xl font-serif text-[#b05d5d] flex items-center mt-8 mb-4"><span class="heading-arrow mr-2">→</span>',
    )

    // Style paragraphs
    .replace(/<p>/g, '<p class="mb-4">')

    // Style lists
    .replace(/<ul>/g, '<ul class="list-disc pl-8 mb-4 space-y-2">')

    // Style list items
    .replace(/<li><p>/g, '<li class="mb-2"><p>')

    // Make sure links have the right color
    .replace(/<a /g, '<a class="text-[#b05d5d]" ')

  return enhancedHtml
}

export default function PrivacyPolicyPage() {
  const [privacyPolicyContent, setPrivacyPolicyContent] = useState<string>("")
  const [isLoading, setIsLoading] = useState<boolean>(true)

  useEffect(() => {
    const fetchPrivacyPolicy = async () => {
      try {
        setIsLoading(true)
        const response = await getPrivacyPolicy()

        // Check if data exists and handle both array and object response formats
        if (response.data && response.data.data) {
          // If data.data is an array
          if (Array.isArray(response.data.data) && response.data.data.length > 0) {
            // Apply the enhancement function to the content
            const enhancedContent = enhanceHtmlContent(response.data.data[0].content)
            setPrivacyPolicyContent(enhancedContent)
          }
          // If data.data is an object (as shown in your API response)
          else if (response.data.data.content) {
            // Apply the enhancement function to the content
            const enhancedContent = enhanceHtmlContent(response.data.data.content)
            setPrivacyPolicyContent(enhancedContent)
          }
        }
      } catch (error) {
        console.error("Failed to fetch privacy policy:", error)
      } finally {
        setIsLoading(false)
      }
    }

    fetchPrivacyPolicy()
  }, [])

  return (
    <div className="bg-[#FBEFEF]">
      <div className="max-w-5xl mx-auto px-4 md:px-6 lg:px-8 pb-6 md:pb-4 lg:pb-4 mt-14 md:mt-14 lg:mt-[4.5rem]">
        <div className="flex items-center justify-center pb-8 pt-12 lg:pt-16 lg:pb-20">
          <img src="/images/privacyPolicy.svg" alt="" className="w-[250px] md:w-[300px] lg:w-[300px]" />
        </div>

        <div className="bg-white p-6 lg:p-14 rounded-lg shadow-sm">
          {isLoading ? (
            <div className="flex justify-center items-center py-10">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#b05d5d]"></div>
            </div>
          ) : privacyPolicyContent ? (
            <div className="privacy-policy-content" dangerouslySetInnerHTML={{ __html: privacyPolicyContent }} />
          ) : (
            <div>
              <h1 className="text-2xl font-serif text-[#b05d5d] mb-4">Privacy Policy for Charmé</h1>
              <p className="mb-4">No privacy policy content has been added yet.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

